import { Component, ElementRef, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { nanoid } from 'nanoid';
import { SegmentRuleCardComponent } from '../segment-rule-card/segment-rule-card.component';
import { SegmentPreviewComponent } from '../segment-preview/segment-preview.component';
import { ISegmentRule } from '../../../../types/contactModule';
import { SegmentService } from '../../../../services/segments/segment-service.service';
import {
  ATTRIBUTE_TEXT,
  OPERATOR_TEXT,
  TSegmentOperator,
  attributeSpec,
  builderCommands,
  isBlankRule,
} from '../segment-utilities';

/** Step 2 of the segment builder: the conditions, with a live count beside them. */
@Component({
  selector: 'segment-rule',
  standalone: true,
  imports: [SegmentRuleCardComponent, SegmentPreviewComponent],
  templateUrl: './segment-rule.component.html',
  styleUrls: ['../segment-builder.css', './segment-rule.component.css'],
})
export class SegmentRuleComponent implements OnInit {
  private router = inject(Router);
  private host: ElementRef<HTMLElement> = inject(ElementRef);
  route = inject(ActivatedRoute);
  segmentService = inject(SegmentService);

  segmentRules: ISegmentRule[] = [this.blankRule()];
  /** One message per row; shown once the person tries to add a row or move on. */
  errors: string[] = [];
  showErrors = false;
  formError = '';

  // getters
  get isEditView() {
    return this.router.url.includes('/segmented-list/edit');
  }

  get routeParamId() {
    return this.route.snapshot.paramMap.get('id');
  }

  createUUID() {
    return nanoid();
  }

  private blankRule(): ISegmentRule {
    return { attributeName: '', rule: '', values: [''], id: this.createUUID(), connector: 'AND' };
  }

  // ── Rows ──
  handleChildUpdate(data: ISegmentRule & { index: number }) {
    const { index, ...rule } = data;
    this.segmentRules = this.segmentRules.map((current, i) => (i === index ? { ...rule, connector: 'AND' } : current));
    this.afterChange();
  }

  /** Adds a row once the open ones are filled in, so blanks never pile up. */
  addAnotherAttribute() {
    const valid = this.validate();
    const blank = this.segmentRules.findIndex((rule) => isBlankRule(rule));
    if (!valid || blank >= 0) {
      if (blank >= 0) {
        this.errors = this.errors.map((error, i) => (i === blank ? 'Fill in this condition first' : error));
      }
      this.showErrors = true;
      this.reportErrors();
      return;
    }
    this.segmentRules = [...this.segmentRules, this.blankRule()];
    this.showErrors = false;
    this.errors = [];
    this.afterChange();
  }

  removeSegmentRule(index: number) {
    this.segmentRules = this.segmentRules.filter((_, i) => i !== index);
    if (!this.segmentRules.length) this.segmentRules = [this.blankRule()];
    this.afterChange();
  }

  private afterChange() {
    this.formError = '';
    this.segmentService.updateSegmentRules(this.segmentRules);
    if (this.showErrors) this.validate();
  }

  // ── Validation ──
  /** Works out each row's problem; returns true when every row can be saved. */
  validate(): boolean {
    const seen = new Map<string, number>();
    const filled = this.segmentRules.filter((rule) => !isBlankRule(rule)).length;
    this.errors = this.segmentRules.map((rule, index) => {
      if (isBlankRule(rule)) {
        return filled === 0 && index === 0 ? 'Choose a field to start your first condition' : '';
      }
      const spec = attributeSpec(rule.attributeName);
      if (!spec) return 'Choose a field';
      if (!rule.rule || !spec.operators.includes(rule.rule as TSegmentOperator)) return 'Choose a comparison';
      const value = (rule.values?.[0] ?? '').toString().trim();
      if (!value) return spec.input === 'gender' || spec.input === 'country' ? 'Choose a value' : 'Enter a value';
      if (spec.input === 'number' && !/^\d{1,3}$/.test(value)) return 'Enter an age in whole years, like 30';
      if (spec.input === 'date' && Number.isNaN(new Date(`${value}T00:00:00`).getTime())) return 'Enter a full date';

      const key = `${spec.label}|${rule.rule}`;
      if (seen.has(key)) {
        const label = `${ATTRIBUTE_TEXT[spec.label]} ${OPERATOR_TEXT[rule.rule as TSegmentOperator]}`;
        return `Condition ${(seen.get(key) ?? 0) + 1} already uses “${label}”. A segment keeps one value per field and comparison, so remove one.`;
      }
      seen.set(key, index);
      return '';
    });
    return this.errors.every((error) => !error);
  }

  /**
   * Says which condition needs fixing (the alert under the list) and moves
   * focus to it, so a press of Add or Next is never silent.
   */
  private reportErrors() {
    const failing = this.errors.map((error, index) => ({ error, index })).filter((item) => !!item.error);
    if (!failing.length) return;
    const first = failing[0];
    const detail = `Condition ${first.index + 1}: ${first.error}${/[.!?]$/.test(first.error) ? '' : '.'}`;
    this.formError =
      failing.length === 1 ? `Fix the condition marked above. ${detail}` : `Fix the ${failing.length} conditions marked above. ${detail}`;
    // After the rows show their errors.
    setTimeout(() => {
      const row = this.host.nativeElement.querySelectorAll<HTMLElement>('segment-rule-card').item(first.index);
      const control =
        row?.querySelector<HTMLElement>('[aria-invalid="true"]:not(:disabled)') ??
        row?.querySelector<HTMLElement>('select:not(:disabled), input:not(:disabled)');
      control?.focus();
    });
  }

  // ── Steps ──
  cancel() {
    this.segmentService.clearSegmentInformation();
    this.router.navigate(['organization', 'contacts', 'segmented-list']);
  }

  goToPrevious() {
    this.segmentService.updateSegmentRules(this.segmentRules);
    this.router.navigate(builderCommands(this.router.url, this.routeParamId), {
      queryParams: { title: 'information' },
    });
  }

  goToReviewPage() {
    this.showErrors = true;
    if (!this.validate()) {
      this.reportErrors();
      return;
    }
    const kept = this.segmentRules.filter((rule) => !isBlankRule(rule));
    this.segmentService.updateSegmentRules(kept);
    this.router.navigate(builderCommands(this.router.url, this.routeParamId), {
      queryParams: { title: 'review' },
    });
  }

  // lifecycle methods
  ngOnInit() {
    const saved = this.segmentService.draftRules;
    if (saved.length > 0) {
      this.segmentRules = saved.map((rule) => ({
        ...rule,
        id: rule.id ?? this.createUUID(),
        values: [...(rule.values?.length ? rule.values : [''])],
        connector: 'AND',
      }));
    }
  }
}
